import type { Site } from '@hotjar/site';
import type { TrackingArgs } from './common/types';
import { trackClick } from './common/track-click';
import * as hotjarTracking from './tools/hotjar';
import { extractSiteGroupingProps, extractPermissionLevel } from './utils';

/**
 * Tracking helper for navigation
 *
 * @private
 */
export const trackNavigationClick = (
  name: string,
  uiPosition: string,
  currentSite?: Site,
): void => {
  const trackingArgs: TrackingArgs = {
    name,
    uiPosition,
    groupData: currentSite ? extractSiteGroupingProps(currentSite) : undefined,
  };

  const permissionLevel = extractPermissionLevel(currentSite);
  if (permissionLevel) {
    trackingArgs.data = {
      permission_level: permissionLevel,
    };
  }

  trackClick(trackingArgs);
};

// reused constants for uiPosition
const POS_PRIMARY_NAV = 'Primary Navigation';
const POS_SECONDARY_NAV = 'Secondary Navigation';
const POS_SITES_ORGS_MENU = `${POS_PRIMARY_NAV} - Sites and Organizations Dropdown Menu`;
const POS_SETTINGS_MENU = `${POS_PRIMARY_NAV} - Settings Dropdown Menu`;
const POS_HELP_MENU = `${POS_PRIMARY_NAV} - Help Dropdown Menu`;
const POS_TEAM_MENU = `${POS_SECONDARY_NAV} - Team Dropdown Menu`;
const POS_SIDEBAR = 'Sidebar';

/**
 * Track user clicks on main logo
 */
export const clickedMainLogo = (currentSite?: Site): void => {
  trackNavigationClick('Main logo', POS_PRIMARY_NAV, currentSite);
};

/**
 * Track hover over site&organizations.
 */
export const hoveredTrackingStateIcon = (): void => {
  hotjarTracking.tagRecording('Hover over tracking state icon');
};

/**
 * Track user clicks on sites & orgs icon.
 */
export const clickedSitesAndOrgsIcon = (currentSite?: Site): void => {
  hotjarTracking.tagRecording('click sites and org');
  hotjarTracking.trigger('clicked_sites_and_orgs');

  trackNavigationClick('Sites and organizations icon dropdown', POS_PRIMARY_NAV, currentSite);
};

/**
 * Track user clicks on sites & orgs settings.
 */
export const clickedSitesAndOrgsSettings = (currentSite?: Site): void => {
  hotjarTracking.tagRecording('click sites and org settings');
  hotjarTracking.trigger('clicked_sites_and_orgs_settings');

  trackNavigationClick('Site and Organization settings', POS_SITES_ORGS_MENU, currentSite);
};

/**
 * Track user clicks on organization search input.
 */
export const clickedSitesAndOrgsSearchbar = (currentSite?: Site): void => {
  trackNavigationClick('Find organization', POS_SITES_ORGS_MENU, currentSite);
};

/**
 * Track user clicks on '+' icon.
 */
export const clickedAddIcon = (currentSite?: Site): void => {
  hotjarTracking.tagRecording('clicks add icon');
  hotjarTracking.trigger('clicked_add_icon');

  trackNavigationClick('Plus dropdown menu', POS_PRIMARY_NAV, currentSite);
};

export enum AddNewSitePosition {
  PLUS_DROPDOWN = 'Plus Dropdown Menu',
  SITES_DROPDOWN = 'Sites and Organizations Dropdown Menu',
}

/**
 * Track user clicks on 'add new site' menu item within the '+' menu.
 */
export const clickedAddNewSite = (position: AddNewSitePosition, currentSite?: Site): void => {
  hotjarTracking.tagRecording('clicks add new site from menu');
  hotjarTracking.trigger('clicked_add_new_site');

  trackNavigationClick('Add new site', `${POS_PRIMARY_NAV} - ${position}`, currentSite);
};

/**
 * Track user clicks on 'add users' menu item with the '+' menu.
 */
export const clickedAddUsers = (currentSite?: Site): void => {
  hotjarTracking.tagRecording('clicks add user from menu');
  hotjarTracking.trigger('clicked_add_users');

  trackNavigationClick('Add team members', `${POS_PRIMARY_NAV} - Plus Dropdown Menu`, currentSite);
};

/**
 * Track user clicks on site name (directs the user to dashboard overview currently).
 */
export const clickedSiteName = (currentSite?: Site): void => {
  hotjarTracking.tagRecording('click site name');
  hotjarTracking.trigger('clicked_site_name');

  trackNavigationClick('Site name', POS_SECONDARY_NAV, currentSite);
};

/**
 * Track user (on Basic) clicks on upsell copy to the right of the plan badge.
 */
export const clickedUpsell = (): void => {
  hotjarTracking.tagRecording('click upsell from header');
  hotjarTracking.trigger('clicked_upsell');
};

/**
 * Track user clicks on 'what's new'.
 */
export const clickedWhatsNew = (currentSite: Site): void => {
  hotjarTracking.tagRecording('click whats new');
  hotjarTracking.trigger('clicked_whats_new');

  trackClick({
    name: 'announcement_new_features',
    uiPosition: 'header_right',
    groupData: extractSiteGroupingProps(currentSite),
  });
};

/**
 * Track user clicks on 'Settings'.
 */
export const clickedSettings = (currentSite?: Site): void => {
  hotjarTracking.tagRecording('click settings from header');
  hotjarTracking.trigger('clicked_settings');

  trackNavigationClick('Settings dropdown', POS_PRIMARY_NAV, currentSite);
};

/**
 * Track user clicks on 'sites and orgs' in settings menu.
 */
export const clickedSitesAndOrgsSettingsDropdown = (currentSite?: Site): void => {
  trackNavigationClick('Sites and organizations', POS_SETTINGS_MENU, currentSite);
};

/**
 * Track user clicks on 'team' in settings menu.
 */
export const clickedTeamSettingsDropdown = (currentSite?: Site): void => {
  trackNavigationClick('Team', POS_SETTINGS_MENU, currentSite);
};

/**
 * Track user clicks on 'User attributes' in settings menu.
 */
export const clickedUserAttributesSettingsDropdown = (currentSite?: Site): void => {
  trackNavigationClick('User attributes', POS_SETTINGS_MENU, currentSite);
};

/**
 * Track user clicks on 'Integrations' in settings menu.
 */
export const clickedIntegrationsSettingsDropdown = (currentSite?: Site): void => {
  trackNavigationClick('Integrations', POS_SETTINGS_MENU, currentSite);
};

/**
 * Track user clicks on 'User lookup' in settings menu.
 */
export const clickedUserLookupSettingsDropdown = (currentSite?: Site): void => {
  trackNavigationClick('User lookup', POS_SETTINGS_MENU, currentSite);
};

/**
 * Track user clicks on 'Preferences' in settings menu.
 */
export const clickedPreferencesSettingsDropdown = (currentSite?: Site): void => {
  trackNavigationClick('Preferences', POS_SETTINGS_MENU, currentSite);
};

/**
 * Track user clicks on 'Single sign on' in settings menu.
 */
export const clickedSSOSettingsDropdown = (currentSite?: Site): void => {
  trackNavigationClick('Single sign-on', POS_SETTINGS_MENU, currentSite);
};

/**
 * Track user clicks on 'Plans and billing' in settings menu.
 */
export const clickedPlansAndBillingSettingsDropdown = (currentSite?: Site): void => {
  trackNavigationClick('Plans and billing', POS_SETTINGS_MENU, currentSite);
};

/**
 * Track user clicks on 'Invoices' in settings menu.
 */
export const clickedInvoicesSettingsDropdown = (currentSite?: Site): void => {
  trackNavigationClick('Invoices', POS_SETTINGS_MENU, currentSite);
};

/**
 * Track user clicks on 'My details' in settings menu.
 */
export const clickedMyDetailsSettingsDropdown = (currentSite?: Site): void => {
  trackNavigationClick('My details', POS_SETTINGS_MENU, currentSite);
};

/**
 * Track user clicks on 'Notifications' in settings menu.
 */
export const clickedNotificationsSettingsDropdown = (currentSite?: Site): void => {
  trackNavigationClick('Notifications', POS_SETTINGS_MENU, currentSite);
};

/**
 * Track user clicks on 'refer friends' in settings menu.
 */
export const clickedReferAndEarnSettingsDropdown = (currentSite?: Site): void => {
  hotjarTracking.tagRecording('click refer and earn - settings');
  hotjarTracking.trigger('clicked_refer_and_earn_settings');

  trackNavigationClick('Refer & Earn', POS_SETTINGS_MENU, currentSite);
};

/**
 * Track user clicks on 'Logout' in settings menu.
 */
export const clickedLogoutSettingsDropdown = (currentSite?: Site): void => {
  trackNavigationClick('Logout', POS_SETTINGS_MENU, currentSite);
};

/**
 * Track user clicks on 'Help'.
 */
export const clickedHelp = (currentSite?: Site): void => {
  hotjarTracking.tagRecording('click help from header');
  hotjarTracking.trigger('clicked_help');

  trackNavigationClick('Help menu dropdown', POS_HELP_MENU, currentSite);
};

/**
 * Track user clicks on 'Get Help'.
 *
 * N.B. only triggers 'Element Clicked' tracking, as others are handled elsewhere.
 */
export const clickedGetHelp = (currentSite?: Site): void => {
  trackNavigationClick('Get Help', POS_HELP_MENU, currentSite);
};

/**
 * Track user clicks on 'Getting started guide'.
 *
 * N.B. only triggers 'Element Clicked' tracking, as others are handled elsewhere.
 */
export const clickedGettingStartedGuide = (currentSite?: Site): void => {
  trackNavigationClick('Getting started guide', POS_HELP_MENU, currentSite);
};

/**
 * Track user clicks on 'System status'.
 *
 * N.B. only triggers 'Element Clicked' tracking.
 */
export const clickedSystemStatus = (currentSite?: Site): void => {
  trackNavigationClick('System status', POS_HELP_MENU, currentSite);
};

/**
 * Track user clicks on Users Badge (has user icon and number of users).
 */
export const clickedUsersBadge = (currentSite?: Site): void => {
  hotjarTracking.tagRecording('click users badge');
  hotjarTracking.trigger('clicked_users_badge');

  trackNavigationClick('Team dropdown', POS_TEAM_MENU, currentSite);
};

/**
 * Track user clicks on "Invite users" link within the dropdown of Users Badge.
 */
export const clickedInviteUsers = (currentSite?: Site): void => {
  hotjarTracking.tagRecording('click invite users from users badge');
  hotjarTracking.trigger('clicked_invite_users');

  trackNavigationClick('Invite team members', POS_TEAM_MENU, currentSite);
};

/**
 * Track user clicks 'send invites' button on the invite users screen.
 */
export const clickedSendInvites = (): void => {
  hotjarTracking.tagRecording('click send invites invite screen');
  hotjarTracking.trigger('clicked_send_invites');
};

/**
 * Track user clicks on 'tracking active' badge.
 */
export const clickedTrackingActive = (): void => {
  hotjarTracking.tagRecording('click tracking active badge');
  hotjarTracking.trigger('clicked_tracking_active');
};

/**
 * Track user clicks on 'tracking inactive' badge.
 */
export const clickedTrackingInactive = (): void => {
  hotjarTracking.tagRecording('click tracking inactive badge');
  hotjarTracking.trigger('clicked_tracking_inactive');
};

/**
 * Track user clicks on sidebar collapse/expand button
 */
export const clickedSidebarCollapse = (isMinimized: boolean, currentSite?: Site): void => {
  trackNavigationClick(
    isMinimized ? 'Expand sidebar' : 'Collapse sidebar',
    POS_SIDEBAR,
    currentSite,
  );
};
